import React, { useCallback, useEffect, useRef, useState } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './BreathGlass.css';

/**
 * Breath on Glass — 창에 서리는 김
 *
 * Mic RMS above a threshold counts as a breath. Fog is a low-res density grid
 * over the pane: breath deposits gaussian blobs in the top third, and the grid
 * spreads by VISCOSITY (a laplacian relaxation) rather than velocity, so it
 * creeps instead of scattering. A value-noise field modulates the deposit so
 * the edge stays organic.
 *
 * The name is not painted on top — it is WIPED OUT of the fog (destination-out)
 * with a condensation ridge (source-atop, so it can only exist where fog is).
 * That means it fades exactly as the fog fades, for free.
 */

// ── palette: achromatic + a very faint sepia. Nothing else. ──
// (the #121314 backdrop lives in BreathGlass.css)
const GLASS_TOP = '#3c4448';      // pale blue-gray pane (near-neutral, cool)
const GLASS_BOTTOM = '#2b3134';
const SEPIA = '218, 206, 186';    // rgb triplet, always used through alpha

const NAMES = ['지원', '엄마', '2007', '민서', '아빠', '하루', '1997', '서연'];

const GRID = 96;                  // fog grid resolution (per side)
const RMS_THRESHOLD = 0.045;      // "숨을 불었다" 판정
// Pacing below is tuned against a headless run of this exact loop: a short puff
// (~0.6s) must NOT summon a name, sustained breathing reaches it at ~1.6s, and
// silence takes ~9s to clear the pane. Changing these changes the whole feel.
const DEPOSIT = 0.4;              // fog added per second at full intensity
const VISCOSITY = 0.16;           // laplacian relaxation per iteration
const VISC_ITERS = 2;
const CREEP = 0.05;               // slight downward bias — fog crawls down the pane
const DECAY_SILENT = 0.22;        // fog lost per second while silent (scaled by density)
const DECAY_BLOWING = 0.05;

const NAME_APPEAR_COV = 0.35;     // coverage needed for a name to start
const NAME_CLEAR_COV = 0.04;      // coverage under which the name is forgotten
const FOG_FOR_COVERAGE = 0.3;     // density counted as "충분히 서림"
const WRITE_MS = 2000;            // stroke-by-stroke duration

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

// ── cheap 2D value noise (hash + smoothstep lerp) ──
function hash2(xi: number, yi: number): number {
  let n = Math.imul(xi, 374761393) + Math.imul(yi, 668265263);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
}
function valueNoise(x: number, y: number): number {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi), b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1), d = hash2(xi + 1, yi + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

type PermState = 'idle' | 'requesting' | 'granted' | 'denied';
type NameState = 'none' | 'writing' | 'settled';

const BreathGlass: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);

  // audio
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioBufRef = useRef<Uint8Array | null>(null);
  const permRef = useRef<PermState>('idle');

  // manual breath — pointer/space. Doubles as the mic-denied fallback.
  const manualRef = useRef(0);

  const [perm, setPerm] = useState<PermState>('idle');
  const [hint, setHint] = useState('창에 숨을 불어 보세요 — 클릭하면 마이크를 사용합니다');

  const exp = experiments.find((e) => e.id === 'breath-glass');

  const start = useCallback(async () => {
    if (permRef.current === 'requesting' || permRef.current === 'granted') return;
    permRef.current = 'requesting';
    setPerm('requesting');
    try {
      let ctx = audioCtxRef.current;
      if (!ctx) {
        ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioCtxRef.current = ctx;
      }
      if (ctx.state === 'suspended') await ctx.resume().catch(() => {});

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.25;
      source.connect(analyser);
      streamRef.current = stream;
      analyserRef.current = analyser;
      audioBufRef.current = new Uint8Array(analyser.fftSize);
      permRef.current = 'granted';
      setPerm('granted');
      setHint('창에 숨을 불어 보세요');
    } catch {
      permRef.current = 'denied';
      setPerm('denied');
      setHint('마이크 없이도 괜찮아요 — 화면을 길게 누르면 입김이 서립니다');
    }
  }, []);

  // ── the whole piece: one loop, all mutable state in refs ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    // offscreen: hi-res fog layer (same logical space as the main canvas)
    const fogLayer = document.createElement('canvas');
    const fogCtx = fogLayer.getContext('2d')!;
    // offscreen: the low-res density grid, upscaled with smoothing for the viscous look
    const gridCanvas = document.createElement('canvas');
    gridCanvas.width = GRID;
    gridCanvas.height = GRID;
    const gridCtx = gridCanvas.getContext('2d')!;
    const gridImage = gridCtx.createImageData(GRID, GRID);

    let side = 0;      // logical square side
    let frame = 0;     // wooden frame thickness
    let gx = 0, gy = 0, gw = 0, gh = 0;   // glass rect (logical)

    const layout = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      side = Math.min(Math.min(vw, vh) * 0.86, 680);
      frame = side * 0.075;
      gx = frame; gy = frame;
      gw = side - frame * 2; gh = side - frame * 2;

      for (const c of [canvas, fogLayer]) {
        c.width = Math.round(side * dpr);
        c.height = Math.round(side * dpr);
      }
      canvas.style.width = `${side}px`;
      canvas.style.height = `${side}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fogCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;
      fogCtx.imageSmoothingEnabled = true;
      fogCtx.imageSmoothingQuality = 'high';
    };
    layout();

    // ── fog state ──
    let density = new Float32Array(GRID * GRID);
    let next = new Float32Array(GRID * GRID);

    // ── name state ──
    let nameText = '';
    let nameState: NameState = 'none';
    let nameT0 = 0;
    let lastName = '';

    const readRms = (): number => {
      const a = analyserRef.current;
      const buf = audioBufRef.current;
      if (!a || !buf || permRef.current !== 'granted') return 0;
      a.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      return Math.sqrt(sum / buf.length);
    };

    // Deposit breath into the top third as drifting gaussian blobs.
    const deposit = (intensity: number, t: number, dt: number) => {
      const blobs = 3;
      for (let b = 0; b < blobs; b++) {
        const ph = b * 2.1;
        // normalized centre inside the glass — stays in the upper third
        const cx = 0.5 + Math.sin(t * 0.55 + ph) * 0.2 + Math.sin(t * 1.31 + ph * 2) * 0.06;
        const cy = 0.17 + Math.sin(t * 0.73 + ph * 1.7) * 0.07 + intensity * 0.05;
        const sigma = (0.1 + intensity * 0.07) * GRID;
        const ccx = cx * GRID, ccy = cy * GRID;
        const r = Math.ceil(sigma * 2.4);
        const inv = 1 / (2 * sigma * sigma);
        const x0 = Math.max(0, Math.floor(ccx - r)), x1 = Math.min(GRID - 1, Math.ceil(ccx + r));
        const y0 = Math.max(0, Math.floor(ccy - r)), y1 = Math.min(GRID - 1, Math.ceil(ccy + r));
        for (let y = y0; y <= y1; y++) {
          const dy = y - ccy;
          for (let x = x0; x <= x1; x++) {
            const dx = x - ccx;
            const g = Math.exp(-(dx * dx + dy * dy) * inv);
            if (g < 0.01) continue;
            // value noise keeps the edge ragged instead of a clean circle
            const n = 0.55 + valueNoise(x * 0.08 + t * 0.25, y * 0.08 - t * 0.12) * 0.9;
            const i = y * GRID + x;
            density[i] = Math.min(1, density[i] + g * n * intensity * DEPOSIT * dt);
          }
        }
      }
    };

    // Viscous spread: relax toward the neighbour average, with a small downward bias.
    const diffuse = (dt: number) => {
      const k = VISCOSITY;
      for (let it = 0; it < VISC_ITERS; it++) {
        for (let y = 0; y < GRID; y++) {
          const yUp = y > 0 ? y - 1 : 0;
          const yDn = y < GRID - 1 ? y + 1 : GRID - 1;
          for (let x = 0; x < GRID; x++) {
            const xL = x > 0 ? x - 1 : 0;
            const xR = x < GRID - 1 ? x + 1 : GRID - 1;
            const i = y * GRID + x;
            const avg =
              (density[yUp * GRID + x] + density[yDn * GRID + x] +
               density[y * GRID + xL] + density[y * GRID + xR]) * 0.25;
            // creep: pull a little extra from the cell above (fog sinks slowly)
            const above = density[yUp * GRID + x];
            next[i] = density[i] + (avg - density[i]) * k + (above - density[i]) * CREEP * dt * 60 * 0.016;
          }
        }
        const tmp = density; density = next; next = tmp;
      }
    };

    const pickName = () => {
      let n = NAMES[Math.floor(Math.random() * NAMES.length)];
      if (NAMES.length > 1) {
        let guard = 0;
        while (n === lastName && guard++ < 8) n = NAMES[Math.floor(Math.random() * NAMES.length)];
      }
      return n;
    };

    // Draw the revealed part of the name as a path on the fog layer.
    // `mode` decides whether we lay the condensation ridge or wipe the centre.
    const strokeName = (progress: number, lineWidth: number) => {
      const fs = gh * 0.2;
      fogCtx.font = `italic 500 ${fs}px 'Snell Roundhand', 'Segoe Script', 'Bradley Hand', 'Apple SD Gothic Neo', 'Noto Sans KR', cursive`;
      fogCtx.textAlign = 'left';
      fogCtx.textBaseline = 'middle';
      fogCtx.lineWidth = lineWidth;
      fogCtx.lineJoin = 'round';
      fogCtx.lineCap = 'round';

      const chars = Array.from(nameText);
      const widths = chars.map((c) => fogCtx.measureText(c).width);
      const total = widths.reduce((a, b) => a + b, 0);
      const startX = gx + (gw - total) / 2;
      const baseY = gy + gh * 0.42;

      let x = startX;
      for (let i = 0; i < chars.length; i++) {
        const w = widths[i];
        const slice = 1 / chars.length;
        const local = clamp((progress - i * slice) / slice, 0, 1);
        if (local <= 0) break;
        fogCtx.save();
        // reveal the glyph left-to-right, as if a finger were dragging across it
        fogCtx.beginPath();
        fogCtx.rect(x - lineWidth, gy, w * local + lineWidth * 2, gh);
        fogCtx.clip();
        fogCtx.strokeText(chars[i], x, baseY);
        fogCtx.restore();
        x += w;
      }
    };

    // ── input: hold to breathe (works with or without a mic) ──
    const press = () => { manualRef.current = 0.22; };
    const release = () => { manualRef.current = 0; };
    const onKeyDown = (e: KeyboardEvent) => { if (e.code === 'Space') { e.preventDefault(); press(); } };
    const onKeyUp = (e: KeyboardEvent) => { if (e.code === 'Space') release(); };
    canvas.addEventListener('pointerdown', press);
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    const onResize = () => layout();
    window.addEventListener('resize', onResize);

    let smooth = 0;
    let last = performance.now();
    const t0 = last;

    const tick = () => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = (now - t0) * 0.001;

      // ── breath ──
      const raw = Math.max(readRms(), manualRef.current);
      smooth = smooth * 0.72 + raw * 0.28;
      const intensity = clamp((smooth - RMS_THRESHOLD) * 7, 0, 1);
      const blowing = intensity > 0;

      if (blowing) deposit(intensity, t, dt);
      diffuse(dt);

      // evaporation — slow while blowing, steady while silent
      const decay = blowing ? DECAY_BLOWING : DECAY_SILENT;
      const f = Math.max(0, 1 - decay * dt);
      let covered = 0;
      for (let i = 0; i < density.length; i++) {
        const d = density[i] * f;
        density[i] = d < 0.002 ? 0 : d;
        if (density[i] > FOG_FOR_COVERAGE) covered++;
      }
      const coverage = covered / density.length;

      // ── name lifecycle (hysteresis: appear high, forget low) ──
      if (nameState === 'none') {
        if (coverage > NAME_APPEAR_COV) {
          nameText = pickName();
          nameState = 'writing';
          nameT0 = now;
        }
      } else if (coverage < NAME_CLEAR_COV) {
        lastName = nameText;
        nameText = '';
        nameState = 'none';
      } else if (nameState === 'writing' && now - nameT0 >= WRITE_MS) {
        nameState = 'settled';
      }

      // ── paint the grid into the small canvas, once, reusing the ImageData ──
      const px = gridImage.data;
      for (let i = 0, p = 0; i < density.length; i++, p += 4) {
        const d = density[i];
        const a = d <= 0 ? 0 : (d * d * (3 - 2 * d)) * 232; // smoothstep → alpha
        px[p] = 226; px[p + 1] = 221; px[p + 2] = 210; px[p + 3] = a;
      }
      gridCtx.putImageData(gridImage, 0, 0);

      // ── compose the fog layer ──
      fogCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fogCtx.globalCompositeOperation = 'source-over';
      fogCtx.clearRect(0, 0, side, side);
      fogCtx.drawImage(gridCanvas, gx, gy, gw, gh);

      if (nameState !== 'none') {
        const p = nameState === 'settled' ? 1 : clamp((now - nameT0) / WRITE_MS, 0, 1);
        // condensation pushed aside by the fingertip — only where fog already is
        fogCtx.globalCompositeOperation = 'source-atop';
        fogCtx.strokeStyle = `rgba(${SEPIA}, 0.55)`;
        strokeName(p, Math.max(4, gh * 0.014));
        // the wipe itself: clear glass shows through
        fogCtx.globalCompositeOperation = 'destination-out';
        fogCtx.strokeStyle = 'rgba(0,0,0,1)';
        strokeName(p, Math.max(1.6, gh * 0.006));
        fogCtx.globalCompositeOperation = 'source-over';
      }

      // ── main canvas ──
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, side, side);

      // glass: cool near-neutral gradient
      const g = ctx.createLinearGradient(0, gy, 0, gy + gh);
      g.addColorStop(0, GLASS_TOP);
      g.addColorStop(1, GLASS_BOTTOM);
      ctx.fillStyle = g;
      ctx.fillRect(gx, gy, gw, gh);

      // a faint diagonal sheen so the pane reads as glass, not paper
      const sheen = ctx.createLinearGradient(gx, gy, gx + gw, gy + gh);
      sheen.addColorStop(0, `rgba(${SEPIA}, 0.05)`);
      sheen.addColorStop(0.45, 'rgba(255,255,255,0)');
      ctx.fillStyle = sheen;
      ctx.fillRect(gx, gy, gw, gh);

      // fog + carved name
      ctx.drawImage(fogLayer, 0, 0, side, side);

      // wooden frame, drawn over the pane edges
      ctx.fillStyle = '#1d1c1a';
      ctx.fillRect(0, 0, side, frame);
      ctx.fillRect(0, side - frame, side, frame);
      ctx.fillRect(0, 0, frame, side);
      ctx.fillRect(side - frame, 0, frame, side);

      // grain — sepia at very low alpha, aged wood
      ctx.save();
      ctx.globalAlpha = 0.5;
      for (let i = 0; i < 26; i++) {
        const n = valueNoise(i * 3.1, 11.7);
        ctx.strokeStyle = `rgba(${SEPIA}, ${0.02 + n * 0.05})`;
        ctx.lineWidth = 0.6 + n * 1.4;
        const off = (i / 26) * side;
        ctx.beginPath();
        ctx.moveTo(off, 0); ctx.lineTo(off + (n - 0.5) * 14, frame);
        ctx.moveTo(off, side - frame); ctx.lineTo(off + (n - 0.5) * 14, side);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, off); ctx.lineTo(frame, off + (n - 0.5) * 14);
        ctx.moveTo(side - frame, off); ctx.lineTo(side, off + (n - 0.5) * 14);
        ctx.stroke();
      }
      ctx.restore();

      // inner bevel: light top-left, shadow bottom-right
      ctx.strokeStyle = `rgba(${SEPIA}, 0.14)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(gx, gy + gh); ctx.lineTo(gx, gy); ctx.lineTo(gx + gw, gy);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(0,0,0,0.45)';
      ctx.beginPath();
      ctx.moveTo(gx + gw, gy); ctx.lineTo(gx + gw, gy + gh); ctx.lineTo(gx, gy + gh);
      ctx.stroke();

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      canvas.removeEventListener('pointerdown', press);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // stop the mic when leaving
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
      analyserRef.current = null;
      streamRef.current = null;
    };
  }, []);

  return (
    <div className="breathGlass" onClick={perm === 'idle' || perm === 'denied' ? start : undefined}>
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />
      <div className="bgStage">
        <canvas ref={canvasRef} className="bgCanvas" />
        <p className={`bgHint ${perm === 'granted' ? 'quiet' : ''}`}>{hint}</p>
      </div>
    </div>
  );
};

export default BreathGlass;
